'use client'

import { useState } from 'react'
import { ChevronDown, CircleHelp } from 'lucide-react'

import { cn } from '@/lib/utils'

export type FAQ = { question: string; answer: string; topic?: string }

export function FaqAccordion({ items }: { items: FAQ[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  return (
    <div className="space-y-3">
      {items.map((item, index) => {
        const isOpen = openIndex === index
        const triggerId = `faq-trigger-${index}`
        const contentId = `faq-content-${index}`
        const displayIndex = String(index + 1).padStart(2, '0')

        return (
          <article
            key={item.question}
            className={cn(
              'group relative overflow-hidden rounded-[8px] border bg-[#0c080b]/80 shadow-xl shadow-black/30 backdrop-blur-sm transition duration-300',
              isOpen
                ? 'border-amber-200/45 bg-[#160d12] ring-1 ring-rose-500/20'
                : 'border-white/10 hover:border-rose-200/35 hover:bg-[#110b10]',
            )}
          >
            <div
              className={cn(
                'absolute inset-y-0 left-0 w-1 transition duration-300',
                isOpen
                  ? 'bg-gradient-to-b from-amber-200 via-rose-500 to-red-950'
                  : 'bg-white/10 group-hover:bg-rose-400/60',
              )}
            />
            <button
              id={triggerId}
              type="button"
              aria-expanded={isOpen}
              aria-controls={contentId}
              className="grid w-full grid-cols-[auto_1fr_auto] items-center gap-3 px-4 py-4 text-left outline-none transition focus-visible:ring-2 focus-visible:ring-amber-200/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#07070a] sm:gap-4 sm:px-5"
              onClick={() => setOpenIndex(isOpen ? null : index)}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px] border font-mono text-[11px] text-amber-100 transition duration-300',
                  isOpen
                    ? 'border-amber-200/40 bg-amber-200/10 shadow-lg shadow-rose-950/50'
                    : 'border-white/10 bg-white/[0.06] group-hover:border-rose-200/35',
                )}
              >
                {displayIndex}
              </span>
              <span className="min-w-0">
                {item.topic ? (
                  <span className="mb-1 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.24em] text-amber-100/60">
                    <CircleHelp className="h-3 w-3" aria-hidden="true" />
                    {item.topic}
                  </span>
                ) : null}
                <span className="block text-base font-semibold leading-snug text-white sm:text-lg">
                  {item.question}
                </span>
              </span>
              <span
                aria-hidden="true"
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] border border-white/10 bg-black/20 text-amber-100 transition duration-300',
                  isOpen && 'border-amber-200/35 bg-amber-200/10',
                )}
              >
                <ChevronDown
                  className={cn(
                    'h-4 w-4 transition-transform duration-300',
                    isOpen && 'rotate-180',
                  )}
                />
              </span>
            </button>
            <div
              id={contentId}
              role="region"
              aria-labelledby={triggerId}
              className={cn(
                'grid transition-all duration-300 ease-out',
                isOpen
                  ? 'grid-rows-[1fr] opacity-100'
                  : 'grid-rows-[0fr] opacity-0',
              )}
            >
              <div className="overflow-hidden">
                <p className="ml-4 border-t border-white/10 px-4 pb-5 pt-4 text-sm leading-7 text-slate-200/80 sm:ml-5 sm:px-5 sm:text-base">
                  {item.answer}
                </p>
              </div>
            </div>
          </article>
        )
      })}
    </div>
  )
}
